import { createAsyncThunk } from '@reduxjs/toolkit';

// functions
import { APIPrefix } from '../utils/functions';
import { fetchComputeJobs } from '../middleware/api/compute_job_apis';

// types
import { DataItemType } from '../utils/types';

//	--------------------------------------------------------------------------
//
//	R E D U X   S T A T E   M A N A G E M E N T
//
//	--------------------------------------------------------------------------

import { store } from './store';

// import state definition and functions.
import {
  DataState,
  rD_setComputeJobs,
  rD_setComputeJobsLoading,
  rD_setStorageAreas,
  rD_updateRucioLocations,
} from './data-slice';

//	--------------------------------------------------------------------------
//
//	T Y P E S
//
//	--------------------------------------------------------------------------

import { Site, SiteStorageArea, StorageArea } from '../utils/types';

//	--------------------------------------------------------------------------
//
//	C O N S T A N T S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	F U N C T I O N S
//
//	--------------------------------------------------------------------------

//	-------------------------------------------------
//
//	builds a list of namespaces & filenames, and
//	a list of sites where that file is staged.
//
//	-------------------------------------------------
console.log('data-actions loaded');

export function fnD_buildRucioLocations(args: {
  items: DataItemType[];
  catalog: string;
  refresh?: boolean;
}) {
  //
  const stateFetch: any = store.getState();
  const stateData: DataState = stateFetch.data;

  var updatedItems: {
    namespace: string;
    filename: string;
    refresh?: boolean | null;
    availableSites?: Site[];
    movingSites?: Site[];
    refreshingLocations?: boolean;
  }[] = [];

  // loop over the selected rows.
  for (var row: number = 0; row < args.items.length; row++) {
    //
    const item: {
      namespace: string;
      filename: string;
    } = args.items[row];

    // ensure this file is not already in the list.
    if (
      updatedItems.findIndex(
        (findItem) =>
          findItem.namespace === item.namespace && findItem.filename === item.filename,
      ) === -1
    ) {
      //
      const rucioLocationsIndex: number = stateData.rucioLocations.findIndex(
        (findRucioItem) =>
          findRucioItem.namespace === item.namespace &&
          findRucioItem.filename === item.filename,
      );

      // get the sites where this file is located.
      var availableSites: Site[] =
        rucioLocationsIndex > -1
          ? Object.assign(
              [],
              stateData.rucioLocations[rucioLocationsIndex].availableSites,
            )
          : [];

      // get the sites where this file is currently being moved to.
      var movingSites: Site[] =
        rucioLocationsIndex > -1
          ? Object.assign([], stateData.rucioLocations[rucioLocationsIndex].movingSites)
          : [];

      // remove any items from the moving-site list if they are already in the
      // available sites list.
      for (var i: number = movingSites.length - 1; i >= 0; i--) {
        if (
          availableSites.findIndex(
            (find) => find.site_uuid === movingSites[i].site_uuid,
          ) > -1
        ) {
          movingSites.splice(i, 1);
        }
      }

      // add a new item.
      var newRow: {
        namespace: string;
        filename: string;
        refresh?: boolean | null;
        availableSites?: Site[];
        movingSites?: Site[];
        refreshingLocations?: boolean;
      } = {
        namespace: item.namespace,
        filename: item.filename,
        availableSites: availableSites,
        movingSites: movingSites,
        refresh: rucioLocationsIndex === -1 && args.refresh !== false,
        refreshingLocations:
          rucioLocationsIndex > -1
            ? stateData.rucioLocations[rucioLocationsIndex].refreshingLocations
            : false,
      };
      updatedItems.push(newRow);
      //
    }
    //
  }

  // update state.
  store.dispatch(
    rD_updateRucioLocations({
      items: updatedItems,
      catalog: args.catalog,
      clearCatalog: true, // clear any existing data for this catalog because we are rebuilding.
    }),
  );
  //
} // fnD_buildRucioLocations

//  ------------------------------------------------------------
//
//  Count the number of data items that are available at the
//  supplied site.
//
//  ------------------------------------------------------------

export function fnD_countAvailableFiles(args: { site: Site; items: DataItemType[] }) {
  //
  const stateFetch: any = store.getState();
  const stateData: DataState = stateFetch.data;

  // filter these items to remove those that are not available at the site.
  let items: DataItemType[] = args.items.filter((itemFilter) => {
    //
    // find the corresponding item in the Rucio locations object.
    let siteAvailable: boolean = false;
    const itemIndex: number = stateData.rucioLocations.findIndex(
      (findItem) =>
        findItem.namespace === itemFilter.namespace &&
        findItem.filename === itemFilter.filename,
    );

    // find the site in the list of available sites.
    if (itemIndex > -1) {
      siteAvailable =
        stateData.rucioLocations[itemIndex].availableSites.findIndex(
          (findSite) => findSite.site_uuid === args.site.site_uuid,
        ) > -1;
    }

    return siteAvailable;
    //
  });

  // return something.
  return items.length;
  //
} // fnD_countAvailableFiles

//	------------------------------------------------------------
//
//	get the Rucio locations for the supplied list of filenames
//	and namespaces.
//
//	------------------------------------------------------------

export const fnD_getAvailableLocations = createAsyncThunk(
  'data/getAvailableLocations',
  async (
    args: {
      items: DataItemType[];
      useAsync?: boolean;
    },
    thunkAPI,
  ) => {
    //
    // get storage locations.
    let rucioLocations: {
      identifier: string;
      associated_storage_area_id: string;
      replicas: string[];
    }[] = [];

    try {
      //
      let urlCommand: string = APIPrefix() + '/v1/data_management/locate_data';

      if (args.useAsync !== true && args.items.length === 1) {
        //
        urlCommand =
          urlCommand +
          '?namespace=' +
          encodeURIComponent(args.items[0].namespace) +
          '&name=' +
          encodeURIComponent(args.items[0].filename);

        const apiResult = await fetch(urlCommand, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });
        if (apiResult.status === 200) {
          //
          const returnedJson = await apiResult.json();

          if (returnedJson.locations !== undefined) {
            rucioLocations = returnedJson.locations;
          }
          //
        }
        //
      } else {
        //
        const apiResult = await fetch(urlCommand, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(args.items),
          credentials: 'include',
        });
        //
      }
      //
    } catch (e) {
      console.log(e);
    }

    // return the storage areas.
    return rucioLocations;
    //
  },
  //
); // fnD_getAvailableLocations

//	------------------------------------------------------------
//
//	An asynchronous function that loads a list of compute
//	jobs from the backend database.
//
//	------------------------------------------------------------

export const fnD_getComputeJobs = createAsyncThunk(
  'data/getComputeJobs',
  async (_, thunkAPI: any) => {
    //
    try {
      thunkAPI.dispatch(rD_setComputeJobsLoading(true));
      const jobs = await fetchComputeJobs();
      thunkAPI.dispatch(rD_setComputeJobs(jobs));
    } catch (error) {
      console.error('Failed to load compute jobs:', error);
      thunkAPI.dispatch(rD_setComputeJobs([]));
    } finally {
      thunkAPI.dispatch(rD_setComputeJobsLoading(false));
    }
    //
  },
  //
); // fnD_getComputeJobs

//	------------------------------------------------------------
//
//	An asynchronous function that loads a list of storage
//	areas from the site-capabilities API.
//
//	------------------------------------------------------------

export async function loadStorageAreas() {
  //
  const stateFetch: any = store.getState();
  const stateData: DataState = stateFetch.data;

  var urlCommand: string = APIPrefix() + '/v1/site_capabilities/list_storage';

  try {
    //
    const res = await fetch(urlCommand, {
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });

    if (res.status === 200) {
      //
      const json = await res.json();

      // Normalize + filter
      const raw: any[] = json?.storage ?? [];
      const filtered = raw.filter((s) => (s.storage_areas?.length ?? 0) > 0);

      const normalized: SiteStorageArea[] = filtered.map((s: any) => ({
        site: stateData.sites.find(
          (find) => find.site_uuid === String(s.site_uuid).toLowerCase(),
        ) ?? { node: '', site: '', site_uuid: '' },
        storage_areas: s.storage_areas,
      }));

      store.dispatch(rD_setStorageAreas(normalized));
      //
    } else if (res.status === 401) {
      console.log('Session expired while loading storage areas.');
      store.dispatch(rD_setStorageAreas([]));
    } else {
      store.dispatch(rD_setStorageAreas([]));
    }
    //
  } catch (e: any) {
    store.dispatch(rD_setStorageAreas([]));
  }
  //
} // loadStorageAreas

export const fnD_getStorageAreas = createAsyncThunk(
  'data/getStorageAreas',
  async (_, thunkAPI: any) => {
    //
    const url = `${APIPrefix()}/v1/site_capabilities/list_storage`;

    try {
      //
      const res = await fetch(url, {
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (res.status === 200) {
        //
        const json = await res.json();

        // Normalize + filter
        const raw: any[] = json?.storage ?? [];
        const filtered = raw.filter((s) => (s.storage_areas?.length ?? 0) > 0);

        const normalized: SiteStorageArea[] = filtered.map((s: any) => ({
          site: {
            node: String(s.node).toUpperCase(),
            site: String(s.site).toUpperCase(),
            site_uuid: String(s.site_uuid).toLowerCase(),
          },
          storage_areas: s.storage_areas,
        }));

        thunkAPI.dispatch(rD_setStorageAreas(normalized));
        //
      } else if (res.status === 401) {
        console.log('Session expired while loading storage areas.');
        thunkAPI.dispatch(rD_setStorageAreas([]));
      } else {
        thunkAPI.dispatch(rD_setStorageAreas([]));
      }
      //
    } catch (e: any) {
      thunkAPI.dispatch(rD_setStorageAreas([]));
    }
    //
  },
  //
); // fnD_getStorageAreas

//	------------------------------------------------------------
//
//	Loop through the list of storage areas, and get the
//	site of the storage ID provided.
//
//	------------------------------------------------------------

export function fnD_getStorageLocationSite(args: { id: string }) {
  //
  const stateFetch: any = store.getState();
  const stateData: DataState = stateFetch.data;

  const site: Site = stateData.storageAreas.find(
    (siteStorage) =>
      siteStorage.storage_areas.find(
        (storageArea) => storageArea.storage_id === args.id,
      ) !== undefined,
  )?.site ?? {
    node: '',
    site: '',
    site_uuid: '',
  };

  // return something.
  return site;
  //
} // fnD_getStorageLocationSite

//	-------------------------------------------------
//
//	receive a list of data locations as storage ids,
//	convert those ids into sites, and update the
//	available locations in the Rucio object.
//
//	-------------------------------------------------

export function fnD_updateDataLocations(args: {
  dataLocationItem: {
    namespace: string;
    filename: string;
    locations: {
      identifier: string;
      associated_storage_area_id: string;
      replicas: string[];
    }[];
  };
}) {
  //
  const stateFetch: any = store.getState();
  const stateData: DataState = stateFetch.data;

  // get a list of sites where this item exists.
  let availableSites: Site[] = stateData.storageAreas
    .filter((filterSite: SiteStorageArea) => {
      //
      let foundMatch: boolean = false;
      let storageAreas: StorageArea[] = filterSite.storage_areas.filter(
        (filterStorageArea: StorageArea) =>
          args.dataLocationItem.locations.findIndex(
            (findStorageArea) =>
              findStorageArea.associated_storage_area_id === filterStorageArea.storage_id,
          ) > -1,
      );
      return storageAreas.length > 0;
      //
    })
    .map((site) => {
      //
      return site.site;
      //
    });

  // prepare the item to be updated.
  const item: {
    namespace: string;
    filename: string;
    refresh?: boolean;
    availableSites?: Site[];
    movingSites?: Site[];
    refreshingLocations?: boolean;
  } = {
    namespace: args.dataLocationItem.namespace,
    filename: args.dataLocationItem.filename,
    availableSites: availableSites,
    refreshingLocations: false,
  };

  // update state.
  store.dispatch(
    rD_updateRucioLocations({
      items: [item],
    }),
  );
  //
} // fnD_updateDataLocations
